import type { LangMap } from "./index";

export const onboarding: LangMap = {
  en: {
    levels: {
      media: "Middle school",
      superiore: "High school",
      universita: "University",
      concorso: "Public exam",
      corso: "Professional course",
    },
    styles: {
      visivo: { l: "Visual", d: "Images, maps, colors" },
      uditivo: { l: "Auditory", d: "Listening, explaining aloud" },
      cinestetico: { l: "Practical", d: "Doing, trying, moving" },
      lettura: { l: "Reading/writing", d: "Summaries and texts" },
    },
    step0: { title: "Hi! What's your name?", subtitle: "I want to get to know you before we start.", label: "Your name" },
    step1: { title: "What path are you on?" },
    step2: { title: "Where do you study?", subtitle: "School, university or faculty — optional.", label: "Institution", placeholder: "E.g. Lincoln High School" },
    step3: { title: "How many hours a day do you study?", subtitle: "Be honest, it doesn't have to be an unrealistic goal." },
    step4: { title: "How do you learn best?" },
    step5: {
      title: "Personalize your learning",
      subtitle: "We'll enable the right accessibility features for you.",
      dsa: "I have a learning disorder (dyslexia, dysgraphia, etc.)",
      adhd: "I have ADHD or difficulty concentrating",
      bes: "I have special educational needs (SEN)",
      none: "I don't have specific needs",
    },
    lang: { title: "Which language should we speak?", subtitle: "You can change it anytime from your profile." },
    back: "Back",
    continue: "Continue",
    saving: "Saving…",
    finish: "Start the journey ✨",
    toasts: { ready: "Profile ready ✨" },
  },
  it: {
    levels: {
      media: "Scuola media",
      superiore: "Scuola superiore",
      universita: "Università",
      concorso: "Concorso",
      corso: "Corso professionale",
    },
    styles: {
      visivo: { l: "Visivo", d: "Immagini, mappe, colori" },
      uditivo: { l: "Uditivo", d: "Ascoltare, spiegare a voce" },
      cinestetico: { l: "Pratico", d: "Fare, provare, muoversi" },
      lettura: { l: "Lettura/scrittura", d: "Riassunti e testi" },
    },
    step0: { title: "Ciao! Come ti chiami?", subtitle: "Voglio conoscerti prima di iniziare.", label: "Il tuo nome" },
    step1: { title: "Che percorso stai facendo?" },
    step2: { title: "Dove studi?", subtitle: "Scuola, università o facoltà — opzionale.", label: "Istituto", placeholder: "Es. Liceo Manzoni" },
    step3: { title: "Quante ore studi al giorno?", subtitle: "Sii onesto, non deve essere un obiettivo irreale." },
    step4: { title: "Come impari meglio?" },
    step5: {
      title: "Personalizza il tuo apprendimento",
      subtitle: "Attiveremo le funzionalità di accessibilità giuste per te.",
      dsa: "Ho un DSA (dislessia, disgrafia, ecc.)",
      adhd: "Ho ADHD o difficoltà di concentrazione",
      bes: "Ho bisogni educativi speciali (BES)",
      none: "Non ho necessità specifiche",
    },
    lang: { title: "In che lingua parliamo?", subtitle: "Puoi cambiarla quando vuoi dal profilo." },
    back: "Indietro",
    continue: "Continua",
    saving: "Salvo…",
    finish: "Inizia il viaggio ✨",
    toasts: { ready: "Profilo pronto ✨" },
  },
  es: {
    levels: {
      media: "Escuela secundaria básica",
      superiore: "Escuela secundaria superior",
      universita: "Universidad",
      concorso: "Oposición",
      corso: "Curso profesional",
    },
    styles: {
      visivo: { l: "Visual", d: "Imágenes, mapas, colores" },
      uditivo: { l: "Auditivo", d: "Escuchar, explicar en voz alta" },
      cinestetico: { l: "Práctico", d: "Hacer, probar, moverse" },
      lettura: { l: "Lectura/escritura", d: "Resúmenes y textos" },
    },
    step0: { title: "¡Hola! ¿Cómo te llamas?", subtitle: "Quiero conocerte antes de empezar.", label: "Tu nombre" },
    step1: { title: "¿Qué recorrido estás haciendo?" },
    step2: { title: "¿Dónde estudias?", subtitle: "Escuela, universidad o facultad — opcional.", label: "Institución", placeholder: "Ej. Instituto Cervantes" },
    step3: { title: "¿Cuántas horas al día estudias?", subtitle: "Sé honesto, no tiene que ser un objetivo irreal." },
    step4: { title: "¿Cómo aprendes mejor?" },
    step5: {
      title: "Personaliza tu aprendizaje",
      subtitle: "Activaremos las funciones de accesibilidad adecuadas para ti.",
      dsa: "Tengo un trastorno del aprendizaje (dislexia, disgrafía, etc.)",
      adhd: "Tengo TDAH o dificultad para concentrarme",
      bes: "Tengo necesidades educativas especiales (NEE)",
      none: "No tengo necesidades específicas",
    },
    lang: { title: "¿En qué idioma hablamos?", subtitle: "Puedes cambiarlo cuando quieras en tu perfil." },
    back: "Atrás",
    continue: "Continuar",
    saving: "Guardando…",
    finish: "Empezar el viaje ✨",
    toasts: { ready: "¡Perfil listo! ✨" },
  },
  fr: {
    levels: {
      media: "Collège",
      superiore: "Lycée",
      universita: "Université",
      concorso: "Concours",
      corso: "Formation professionnelle",
    },
    styles: {
      visivo: { l: "Visuel", d: "Images, cartes, couleurs" },
      uditivo: { l: "Auditif", d: "Écouter, expliquer à voix haute" },
      cinestetico: { l: "Pratique", d: "Faire, essayer, bouger" },
      lettura: { l: "Lecture/écriture", d: "Résumés et textes" },
    },
    step0: { title: "Salut ! Comment tu t'appelles ?", subtitle: "Je veux te connaître avant de commencer.", label: "Ton nom" },
    step1: { title: "Quel parcours suis-tu ?" },
    step2: { title: "Où étudies-tu ?", subtitle: "École, université ou faculté — optionnel.", label: "Établissement", placeholder: "Ex. Lycée Voltaire" },
    step3: { title: "Combien d'heures étudies-tu par jour ?", subtitle: "Sois honnête, cela ne doit pas être un objectif irréaliste." },
    step4: { title: "Comment apprends-tu le mieux ?" },
    step5: {
      title: "Personnalise ton apprentissage",
      subtitle: "Nous activerons les bonnes fonctionnalités d'accessibilité pour toi.",
      dsa: "J'ai un trouble de l'apprentissage (dyslexie, dysgraphie, etc.)",
      adhd: "J'ai un TDAH ou des difficultés de concentration",
      bes: "J'ai des besoins éducatifs particuliers",
      none: "Je n'ai pas de besoins spécifiques",
    },
    lang: { title: "Dans quelle langue parle-t-on ?", subtitle: "Tu peux la changer à tout moment dans ton profil." },
    back: "Retour",
    continue: "Continuer",
    saving: "Enregistrement…",
    finish: "Commencer le voyage ✨",
    toasts: { ready: "Profil prêt ✨" },
  },
  de: {
    levels: {
      media: "Mittelschule",
      superiore: "Oberschule",
      universita: "Universität",
      concorso: "Wettbewerb/Prüfung",
      corso: "Berufskurs",
    },
    styles: {
      visivo: { l: "Visuell", d: "Bilder, Karten, Farben" },
      uditivo: { l: "Auditiv", d: "Zuhören, laut erklären" },
      cinestetico: { l: "Praktisch", d: "Tun, ausprobieren, bewegen" },
      lettura: { l: "Lesen/Schreiben", d: "Zusammenfassungen und Texte" },
    },
    step0: { title: "Hallo! Wie heißt du?", subtitle: "Ich möchte dich kennenlernen, bevor wir beginnen.", label: "Dein Name" },
    step1: { title: "Welchen Bildungsweg gehst du?" },
    step2: { title: "Wo lernst du?", subtitle: "Schule, Universität oder Fakultät — optional.", label: "Einrichtung", placeholder: "Z.B. Goethe-Gymnasium" },
    step3: { title: "Wie viele Stunden am Tag lernst du?", subtitle: "Sei ehrlich, es muss kein unrealistisches Ziel sein." },
    step4: { title: "Wie lernst du am besten?" },
    step5: {
      title: "Personalisiere dein Lernen",
      subtitle: "Wir aktivieren die richtigen Barrierefreiheitsfunktionen für dich.",
      dsa: "Ich habe eine Lernstörung (Legasthenie, Dysgraphie usw.)",
      adhd: "Ich habe ADHS oder Konzentrationsschwierigkeiten",
      bes: "Ich habe besondere pädagogische Bedürfnisse",
      none: "Ich habe keine besonderen Bedürfnisse",
    },
    lang: { title: "In welcher Sprache sprechen wir?", subtitle: "Du kannst sie jederzeit im Profil ändern." },
    back: "Zurück",
    continue: "Weiter",
    saving: "Speichern…",
    finish: "Reise beginnen ✨",
    toasts: { ready: "Profil bereit ✨" },
  },
  pt: {
    levels: {
      media: "Ensino fundamental",
      superiore: "Ensino médio",
      universita: "Universidade",
      concorso: "Concurso público",
      corso: "Curso profissional",
    },
    styles: {
      visivo: { l: "Visual", d: "Imagens, mapas, cores" },
      uditivo: { l: "Auditivo", d: "Ouvir, explicar em voz alta" },
      cinestetico: { l: "Prático", d: "Fazer, experimentar, mover-se" },
      lettura: { l: "Leitura/escrita", d: "Resumos e textos" },
    },
    step0: { title: "Olá! Qual é o seu nome?", subtitle: "Quero te conhecer antes de começarmos.", label: "Seu nome" },
    step1: { title: "Qual percurso você está seguindo?" },
    step2: { title: "Onde você estuda?", subtitle: "Escola, universidade ou faculdade — opcional.", label: "Instituição", placeholder: "Ex. Colégio Dom Pedro" },
    step3: { title: "Quantas horas por dia você estuda?", subtitle: "Seja honesto, não precisa ser uma meta irreal." },
    step4: { title: "Como você aprende melhor?" },
    step5: {
      title: "Personalize seu aprendizado",
      subtitle: "Ativaremos os recursos de acessibilidade certos para você.",
      dsa: "Tenho um transtorno de aprendizagem (dislexia, disgrafia, etc.)",
      adhd: "Tenho TDAH ou dificuldade de concentração",
      bes: "Tenho necessidades educacionais especiais",
      none: "Não tenho necessidades específicas",
    },
    lang: { title: "Em que idioma falamos?", subtitle: "Podes mudar quando quiseres no perfil." },
    back: "Voltar",
    continue: "Continuar",
    saving: "Salvando…",
    finish: "Começar a jornada ✨",
    toasts: { ready: "Perfil pronto ✨" },
  },
};
